import { inject, Injectable } from '@angular/core'
import { AdminService } from '@app/srv/admin.service'
import { getState } from '@app/srv/store.service'
import { logUtil } from '@app/util/log.util'
import {
  Adjust,
  AdjustConfig,
  AdjustEnvironment,
  AdjustEvent,
  AdjustEventFailure,
  AdjustLogLevel,
} from '@awesome-cordova-plugins/adjust/ngx'
import { Platform } from '@ionic/angular/standalone'
import { ExperimentalPrice } from '@naturalcycles/shared'
import { env } from '@src/environments/environment.js'
import { waitFor } from '../util/wait.util'
import { MixPanelService } from './mixpanel.service'

@Injectable({ providedIn: 'root' })
export class AdjustService {
  private isLoaded = false
  private isEnabled = false

  private platform = inject(Platform)
  private adjust = inject(Adjust)
  private adminService = inject(AdminService)
  private mixpanelService = inject(MixPanelService)

  /**
   * Initializes and enables Adjust SDK if conditions are met, disables it otherwise.
   */
  public async revalidate(): Promise<void> {
    const { account } = getState()
    const shouldBeEnabled =
      !account.nextPaymentDate &&
      !account.completeDate &&
      !!account.marketingSocialConsent &&
      !(!env.analyticsForAdmins && this.adminService.isAdmin() && !this.adminService.isTestUser())

    if (shouldBeEnabled && !this.isLoaded) this.init()

    await this.setEnabled(shouldBeEnabled)
  }

  public trackEvent(eventToken: string, price?: ExperimentalPrice): void {
    if (!this.isLoaded || !this.isEnabled) return

    const e = new AdjustEvent(eventToken)
    if (price) e.setRevenue(price.toUnits(), price.currency)
    e.addCallbackParameter('ncAccountId', getState().account.externalPersonalId)

    this.adjust.trackEvent(e)
  }

  /**
   * While `adjust.setEnabled` is synchronous, it doesn't actually set the value immediately.
   * We wait for the setter to have taken effect, and then update our internal state.
   */
  private async setEnabled(enabled: boolean): Promise<void> {
    if (!this.isLoaded) return

    if (enabled) this.adjust.enable()
    else this.adjust.disable()

    await waitFor(
      'adjust.isEnabled',
      async () => await this.adjust.isEnabled(),
      isEnabled => isEnabled === enabled,
      50,
      100,
    )
    this.isEnabled = enabled
  }

  private init(): void {
    if (!this.platform.is('hybrid') || !env.adjustApiToken) return

    const config = new AdjustConfig(
      env.adjustApiToken,
      env.adjustProd ? AdjustEnvironment.Production : AdjustEnvironment.Sandbox,
    )
    // config.setLogLevel(AdjustLogLevel.Verbose) // use for debugging
    config.setLogLevel(AdjustLogLevel.Info)
    config.setEventTrackingFailedCallback((e: AdjustEventFailure) => {
      // alert('adjust event failed: ' + e.eventToken)

      logUtil.log('adjust.service adjust event failed', { adjustEventFailure: e })
    })

    this.adjust.initSdk(config)
    this.adjust.addGlobalCallbackParameter('ncDistinctId', this.mixpanelService.getDistinctId()!)
    this.isLoaded = true
  }
}
